import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useData } from '../../context/DataContext';
import { getIcon } from '../../utils/iconMap';
import '../../styles/pages/Contact.css';

const Contact = () => {
  const { data, getContact, getUIText, loading, error } = useData();
  const contact = getContact();
  const contactUI = getUIText('contact');
  const backgroundImage = data?.images?.backgrounds?.contact;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSubmitError('');

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_USER_ID;

    if (!serviceId || !templateId || !publicKey) {
      setSubmitError('Email service not configured. Please contact us directly.');
      setSending(false);
      return;
    }

    try {
      await emailjs.send(serviceId, templateId, {
        user_name: formData.name,
        user_email: formData.email,
        user_phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      }, publicKey);

      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setSubmitError('Failed to send message. Please try again or contact us directly.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-container"><p>Error loading data</p></div>;
  }

  const heroStyle = backgroundImage ? {
    '--page-hero-bg': `url(${backgroundImage})`
  } : {};

  return (
    <main className="contact-page mandala-bg">
      <section className={`page-hero${backgroundImage ? ' has-bg-image' : ''}`} style={heroStyle}>
        <div className="container">
          <h1 className="page-title">{contactUI?.pageTitle || 'Contact Us'}</h1>
          <p className="page-subtitle">{contactUI?.pageSubtitle}</p>
        </div>
      </section>

      <section className="section contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <h2 className="info-title">{contactUI?.infoSection?.title || 'Get in Touch'}</h2>
              <p className="info-subtitle">{contactUI?.infoSection?.subtitle}</p>

              <ul className="contact-details">
                {contact?.address && (
                  <li className="contact-item">
                    <div className="contact-icon">{getIcon('FaLocationDot')}</div>
                    <div className="contact-text">
                      <strong>{contactUI?.locationLabel || 'Our Location'}</strong>
                      <span>
                        {contact.address.street}, {contact.address.city}<br />
                        {contact.address.state} - {contact.address.zipCode}, {contact.address.country}
                      </span>
                      {contact.mapLink && (
                        <a
                          href={contact.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-map-link"
                        >
                          View on Maps
                        </a>
                      )}
                    </div>
                  </li>
                )}
                {contact?.phone && (
                  <li className="contact-item">
                    <div className="contact-icon">{getIcon('FaPhone')}</div>
                    <div className="contact-text">
                      <strong>{contactUI?.phoneLabel || 'Phone Number'}</strong>
                      <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                      {contact.alternatePhone && (
                        <a href={`tel:${contact.alternatePhone}`}>{contact.alternatePhone}</a>
                      )}
                    </div>
                  </li>
                )}
                {contact?.email && (
                  <li className="contact-item">
                    <div className="contact-icon">{getIcon('FaEnvelope')}</div>
                    <div className="contact-text">
                      <strong>{contactUI?.emailLabel || 'Email Address'}</strong>
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </div>
                  </li>
                )}
                {contact?.businessHours && (
                  <li className="contact-item">
                    <div className="contact-icon">{getIcon('FaClock')}</div>
                    <div className="contact-text">
                      <strong>{contactUI?.hoursLabel || 'Business Hours'}</strong>
                      <span>Weekdays: {contact.businessHours.weekdays}</span>
                      <span>Weekends: {contact.businessHours.weekends}</span>
                    </div>
                  </li>
                )}
              </ul>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2 className="form-title">{contactUI?.formSection?.title || 'Send Us a Message'}</h2>

              {submitted && (
                <div className="success-message">
                  {contactUI?.formSection?.successMessage || 'Thank you! We\'ll get back to you shortly.'}
                </div>
              )}

              {submitError && (
                <div className="error-message">
                  {submitError}
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                <button type="submit" className="submit-btn" disabled={sending}>
                  {sending ? 'Sending...' : (data?.ui?.global?.submitButton || 'Send Message')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
